import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Grid,
  Rating,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import color from "../utils/Colors";
import { Info, ShoppingCart } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CardPopup from "../modal/CardPopup";
import {
  addToCart,
  addToWishlist,
} from "../../services/services";
import { useUser } from "../../services/Context/UserContext";
import { useSnackbar } from "../utils/SnackBar";
const PUBLIC_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL

interface CourseCardProps {
  courseName: string;
  teachers: string[];
  localRating: number;
  globalRating: number;
  price: number;
  level: string;
  duration: string;
  image: string;
  courseId: number;
  description?: string;
  whoCanJoin?: string;
  learningObjectives?: string[];
  unitOfPrice?: string;
}

const CourseCard: React.FC<CourseCardProps> = ({
  courseName,
  teachers,
  localRating,
  globalRating,
  price,
  level,
  duration,
  image,
  courseId,
  description,
  whoCanJoin,
  learningObjectives,
  unitOfPrice,
}) => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { user } = useUser();

  const [open, setOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addingToWishlist, setAddingToWishlist] = useState(false);

  const computedLocalRating = localRating ?? 0;
  const computedGlobalRating = globalRating ?? 0;

  // Build real data for the popup from the props
  const popupData = {
    title: courseName,
    updatedDate: "",
    hours: duration || "",
    level: level || "",
    subtitles: "",
    description: description || whoCanJoin || "",
    points: learningObjectives ?? [],
  };

  const handleAddToCart = async () => {
    if (!user?.id) {
      showSnackbar("Please login to add to cart");
      navigate("/login");
      return;
    }
    setAddingToCart(true);
    try {
      await addToCart({
        productId: courseId,
        userId: user.id,
        status: "active",
      });
      showSnackbar("Added to cart successfully!");
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.msg;
      showSnackbar(msg || "Failed to add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleAddToWishlist = async () => {
    if (!user?.id) {
      showSnackbar("Please login to add to wishlist");
      navigate("/login");
      return;
    }
    setAddingToWishlist(true);
    try {
      await addToWishlist({ courseId, userId: user.id });
      showSnackbar("Added to wishlist!");
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.msg;
      showSnackbar(msg || "Failed to add to wishlist");
    } finally {
      setAddingToWishlist(false);
    }
  };
  return (
    <Card
      sx={{
        maxWidth: 300,
        boxShadow: "10px 20px 20px rgba(0, 0, 0, 0.04)",
        borderRadius: "10px",
        position: "relative",
        minWidth: 280,
      }}
    >
      <Info
        onClick={() => setOpen(true)}
        sx={{
          zIndex: 1000,
          background: "white",
          position: "absolute",
          top: "0px",
          right: "0px",
          padding: "4px",
          width: "30px",
          height: "30px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.4)",
          borderBottomLeftRadius: "8px",
          color: color.firstColor,
          transition: "width 0.3s ease, height 0.3s ease",
          "&:hover": { width: "35px", height: "35px" },
          cursor: "pointer",
        }}
      />

      <CardPopup
        open={open}
        handleClose={() => setOpen(false)}
        data={popupData}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleAddToWishlist}
        addingToCart={addingToCart}
        addingToWishlist={addingToWishlist}
      />

      <CardMedia component="img" height="180" image={PUBLIC_BASE_URL+image} alt={courseName} />
      <CardContent>
        <Typography
          onClick={() => navigate(`/course/${courseId}`)}
          gutterBottom
          variant="h5"
          component="div"
          sx={{
            color: color.textColor,
            mt: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          fontWeight="bold"
        >
          {courseName}
        </Typography>

        <Typography variant="body2" color="textSecondary" mb={1}>
          <strong>Teachers Name:</strong>
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              {teachers}
            </Typography>
          </Grid>
        </Grid>

        <Box my={2}>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ display: "flex", alignItems: "center", mb: 1 }}
          >
            Local rating:
            <Rating
              value={computedLocalRating}
              precision={0.5}
              readOnly
              icon={<StarIcon fontSize="inherit" />}
              emptyIcon={<StarBorderIcon fontSize="inherit" />}
              sx={{ color: color.textColor, ml: 1.7 }}
            />
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ display: "flex", alignItems: "center" }}
          >
            Global rating:
            <Rating
              value={computedGlobalRating}
              precision={0.5}
              readOnly
              icon={<StarIcon fontSize="inherit" />}
              emptyIcon={<StarBorderIcon fontSize="inherit" />}
              sx={{ color: color.textColor, ml: 1 }}
            />
          </Typography>
        </Box>

        <Divider sx={{ my: 2, borderColor: color.firstColor }} />

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection="row-reverse"
          sx={{ mt: 1 }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ gap: "10px", width: "50%" }}
          >
            <Typography variant="h6" sx={{ color: color.textColor }}>
              Price: {unitOfPrice === "INR" ? "₹" : "$"}
              {price}
            </Typography>

            <Button
              onClick={handleAddToCart}
              disabled={addingToCart}
              style={{
                background: color.firstColor,
                color: "white",
                borderRadius: "8px",
                textTransform: "none",
                transition: "transform 0.3s ease",
                minWidth: "120px",
              }}
            >
              {addingToCart ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                <>
                  Add to Cart <ShoppingCart sx={{ ml: 1 }} />
                </>
              )}
            </Button>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="flex-start"
            flexDirection="column"
            sx={{ gap: "10px", width: "50%", pl: 2 }}
          >
            <Box display="flex" alignItems="center">
              <SignalCellularAltIcon fontSize="small" />
              <Typography
                variant="body2"
                ml={1}
                sx={{ color: color.textColor }}
              >
                {level}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <AccessTimeIcon fontSize="small" />
              <Typography
                variant="body2"
                ml={1}
                sx={{ color: color.textColor }}
              >
                {duration}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CourseCard;
